import { useNavigate, useRouter, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useRef } from 'react'
import { useChainId, useSwitchChain } from 'wagmi'
import { tempo, tempoLocalnet, tempoModerato } from 'wagmi/chains'

import { localTempoEnabled, shouldDefaultToTestnet } from './chain.js'

/** Returns testnet state and a toggle action for the active wagmi chain. */
export function useTestnet() {
  const chainId = useChainId()
  const switchChain = useSwitchChain()

  const isTestnet = chainId === tempoModerato.id
  const isLocalnet = localTempoEnabled && chainId === tempoLocalnet.id
  const toggle = useCallback(
    () => switchChain.mutate({ chainId: isTestnet ? tempo.id : tempoModerato.id }),
    [isTestnet, switchChain],
  )
  const switchToLocalnet = useCallback(
    () => switchChain.mutate({ chainId: tempoLocalnet.id }),
    [switchChain],
  )
  const switchToMainnet = useCallback(
    () => switchChain.mutate({ chainId: tempo.id }),
    [switchChain],
  )

  return {
    isLocalnet,
    isTestnet,
    localTempoEnabled,
    switchToLocalnet,
    switchToMainnet,
    toggle,
  } as const
}

/** Keeps the `?testnet` URL flag and active wagmi chain synchronized. */
export function useSyncTestnet() {
  const { testnet } = useSearch({ from: '/_app' })
  const chainId = useChainId()
  const defaultToTestnet = shouldDefaultToTestnet()
  const isTestnet = chainId === tempoModerato.id
  const navigate = useNavigate()
  const router = useRouter()
  const switchChain = useSwitchChain()
  const isLocalnet = localTempoEnabled && chainId === tempoLocalnet.id
  const targetTestnet = testnet ?? defaultToTestnet
  const target = targetTestnet ? tempoModerato.id : tempo.id
  const settled = useRef(chainId === target || isLocalnet)
  const switching = useRef<number | null>(null)

  function sync(chainId_: typeof tempo.id | typeof tempoLocalnet.id | typeof tempoModerato.id) {
    if (switching.current === chainId_) return
    settled.current = false
    switching.current = chainId_
    switchChain.mutate(
      { chainId: chainId_ },
      {
        onSettled: () => {
          if (switching.current === chainId_) switching.current = null
        },
        onSuccess: () => {
          if (switching.current === chainId_) settled.current = true
        },
      },
    )
  }

  // URL/default → wagmi: seed on mount and re-sync when the URL changes externally.
  useEffect(() => {
    if (isLocalnet) {
      settled.current = true
      return
    }
    if (chainId === target) {
      settled.current = true
      return
    }
    sync(target)
  }, [target])

  useEffect(() => {
    if (chainId === target) settled.current = true
  }, [chainId, target])

  // The local testnet default only applies until the URL records a user choice.
  useEffect(() => {
    if (testnet !== undefined) return
    if (isLocalnet) return
    if (!defaultToTestnet || chainId === tempoModerato.id) return
    sync(tempoModerato.id)
  }, [chainId, defaultToTestnet, isLocalnet, testnet])

  // wagmi → URL: mirror direct switchChain calls from hotkeys, menus, and status pills.
  useEffect(() => {
    if (!settled.current) return
    const nextTestnet = isLocalnet
      ? undefined
      : isTestnet
        ? true
        : defaultToTestnet
          ? false
          : undefined
    if (testnet === nextTestnet) return
    navigate({
      replace: true,
      search: { ...router.state.location.search, testnet: nextTestnet },
    } as never)
  }, [isLocalnet, isTestnet])
}
